// AGC: LotCreationWizard - Orchestrates lot creation screens during onboarding
// AGC: Renders outside React Router tree (same pattern as VoiceCalibration in App.tsx)
import React, { useState, useCallback } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { motion, AnimatePresence } from 'framer-motion'
import { NovoLote } from '../fields/NovoLote'
import { CreateWithGPS } from '../fields/CreateWithGPS'
import { PolygonDrawingMap } from '../fields/PolygonDrawingMap'
import { CropSuggestions } from '../fields/CropSuggestions'
import { FieldTaskPlanning } from '../fields/FieldTaskPlanning'
import type { Field, Screen } from '../../types'
import type { PlotFormData } from '../../types/navigation'

interface LotCreationWizardProps {
  farm: Field
  onComplete: () => void
  onSkip: () => void
}

type WizardPhase =
  | 'lot-name'
  | 'gps'
  | 'polygon-map'
  | 'crop-suggestions'
  | 'task-planning'

export const LotCreationWizard: React.FC<LotCreationWizardProps> = ({
  farm,
  onComplete,
  onSkip,
}) => {
  const { t } = useTranslation()
  const [phase, setPhase] = useState<WizardPhase>('lot-name')
  const [lotName, setLotName] = useState('')
  const [plotData, setPlotData] = useState<PlotFormData | null>(null)

  // AGC: Handle navigation calls from lot screens — translate Screen names to wizard phases
  const handleNavigate = useCallback((screen: Screen, data?: any) => {
    if (screen === 'create-with-gps') {
      setLotName(data?.lotName || '')
      setPhase('gps')
    } else if (screen === 'polygon-map') {
      setLotName(data?.lotName || '')
      setPhase('polygon-map')
    } else if (screen === 'crop-suggestions') {
      if (data?.plotData) {
        setPlotData(data.plotData)
      }
      setPhase('crop-suggestions')
    } else if (screen === 'field-task-planning') {
      if (data?.plotData) {
        setPlotData(data.plotData)
      }
      setPhase('task-planning')
    }
  }, [])

  // AGC: Handle back navigation within the wizard
  const handleBack = useCallback(() => {
    switch (phase) {
      case 'gps':
      case 'polygon-map':
        setPhase('lot-name')
        break
      case 'crop-suggestions':
        setPhase('lot-name')
        break
      case 'task-planning':
        setPhase('crop-suggestions')
        break
      default:
        break
    }
  }, [phase])

  // AGC: Handle GPS save — build PlotFormData and go to crop suggestions
  const handleGPSSave = useCallback((data: { name: string; coordinates: [number, number][]; area: number; perimeter: number }) => {
    const generatedId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newPlotData: PlotFormData = {
      id: generatedId,
      name: lotName || data.name || t('fields.newLot'),
      area: data.area,
      cropType: '',
      coordinates: data.coordinates,
      fieldId: farm.id,
      perimeter: data.perimeter,
      detectionMethod: 'gps',
    }
    setPlotData(newPlotData)
    setPhase('crop-suggestions')
  }, [lotName, farm.id])

  // AGC: Handle polygon map save — build PlotFormData and go to crop suggestions
  const handlePolygonSave = useCallback((polygon: { name?: string; coordinates: [number, number][]; area: number; perimeter: number }) => {
    const generatedId = `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newPlotData: PlotFormData = {
      id: generatedId,
      name: lotName || polygon.name || t('fields.newLot'),
      area: polygon.area,
      cropType: '',
      coordinates: polygon.coordinates,
      fieldId: farm.id,
      perimeter: polygon.perimeter,
      detectionMethod: 'polygon',
    }
    setPlotData(newPlotData)
    setPhase('crop-suggestions')
  }, [lotName, farm.id])

  const renderPhase = () => {
    switch (phase) {
      case 'lot-name':
        return (
          <NovoLote
            onNavigate={handleNavigate}
            onBack={onSkip}
            initialFarmId={farm.id}
            wizardMode
            wizardStep={1}
            onWizardSkip={onSkip}
          />
        )
      case 'gps':
        return (
          <CreateWithGPS
            lotName={lotName || 'Novo lote'}
            onCancel={() => setPhase('lot-name')}
            onSave={handleGPSSave}
          />
        )
      case 'polygon-map':
        return (
          <PolygonDrawingMap
            lotName={lotName}
            onCancel={() => setPhase('lot-name')}
            onSave={handlePolygonSave}
          />
        )
      case 'crop-suggestions':
        return (
          <CropSuggestions
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleNavigate}
            onBack={handleBack}
            wizardMode
            wizardStep={3}
          />
        )
      case 'task-planning':
        return (
          <FieldTaskPlanning
            plotData={plotData || { name: '', area: 0, cropType: '' }}
            onNavigate={handleNavigate}
            onBack={handleBack}
            wizardMode
            wizardStep={4}
            onWizardComplete={onComplete}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className="h-[100dvh] bg-[#F4F5F7] dark:bg-slate-900 flex flex-col overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={phase}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col overflow-y-auto"
        >
          {renderPhase()}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
